import './FormInput.css';

import InputLabel from '../../../InputLabel/InputLabel';
import GridInput, { GridInputProps } from '../../../GridInput/GridInput';
import { GridInputValue, ReferenceControl } from '../../../../../types/types';
import { useDatasetFormInputData } from '../../../../../hooks/context/useDatasetFormInputData';
import { useDatasetFormData } from '../../../../../hooks/context/useDatasetFormData';
import { useFormHandlersContext } from '../../../../../hooks/context/useFormHandlersContext';
import { useRequestDataContext } from '../../../../../hooks/context/useRequestDataContext';
import { FormApiMethodsRequestData } from '../../../../../api/types/types';
import { CSSProperties } from 'react';
import { FORM_ITEM_FLEX_WIDTH_FALLBACK } from './constants';
import {
  withFormVisibilityInit,
  WithFormVisibilityInitProp,
} from '../../hoc/withFormVisibilityInit';
import { FormItem } from '../../../../../types/types';
import {
  withEditableResolve,
  WithEditableResolveProp,
} from '../../../../../hoc/withEditableResolve';
import { getFormFieldControlType } from '../../utils/getFormFieldControlType';

type FormInputProps = Pick<
  GridInputProps,
  | 'name'
  | 'label'
  | 'type'
  | 'control'
  | 'setReferenceDatasets'
  | 'referenceDatasets'
> &
  Pick<
    FormItem,
    | 'allowVisible'
    | 'allowEditable'
    | 'mandatory'
    | 'width'
    | 'hint'
    | 'help'
    | 'handlerTriggers'
  > & {
    visible: boolean;
    editable: boolean;
    resolveData: WithFormVisibilityInitProp['resolveData'];
    openFullscreenReference: (
      name: string,
      datasetId: string,
      control: ReferenceControl,
      value: string,
      label: string,
      // resolvedFilter forwarded from ReferenceInput's click-time JSONata pass.
      // null sentinel = resolved to nothing (strip raw filter).
      resolvedFilter?: Record<string, any> | null
    ) => void;
  };

export type FormInputHOCProps = FormInputProps &
  WithFormVisibilityInitProp &
  WithEditableResolveProp;

const FormInput = ({
  name,
  label,
  hint,
  help,
  type,
  control,
  editable,
  allowEditable,
  mandatory,
  width,
  handlerTriggers,
  referenceDatasets,
  visible,
  allowVisible,
  setReferenceDatasets,
  openFullscreenReference,
}: FormInputProps) => {
  const { handleInputChange, value, dataset, error, setError } =
    useDatasetFormInputData(name);
  const { getFieldCallbacks } = useFormHandlersContext();
  // Read form definition and full form values for ReferenceInput JSONata filter resolution
  const { form, formData } = useDatasetFormData();
  // Cast: in form scope requestData is always FormApiMethodsRequestData
  const requestData = useRequestDataContext() as FormApiMethodsRequestData | undefined;

  const { onChange, onTouch, onFocusIn, onFocusOut } = getFieldCallbacks(
    name,
    control,
    handlerTriggers
  );

  const isLiteralText = control?.type === 'literalText';
  const showLabel = type !== 'boolean' && (!isLiteralText || !!label);
  const resolvedLabel = label || name;
  const resolvedVisibility = visible && allowVisible;
  const fieldControlType = getFormFieldControlType(control, type);

  // resolvedFilter: pre-resolved control.filter from JSONata; undefined = not resolved, null = resolved to nothing (strip raw filter)
  const handleOpenFullscreenReference = (
    resolvedFilter?: Record<string, any> | null
  ) => {
    if (dataset && label) {
      openFullscreenReference(
        name,
        dataset.datasetId,
        control as ReferenceControl,
        value,
        label,
        resolvedFilter
      );
    }
  };

  const handleChange = (
    nextValue: GridInputValue,
    propagateHandler: boolean = true
  ) => {
    handleInputChange(name, nextValue);
    // Skip firing the field-level handler for programmatic writes
    // (e.g. mount-time defaults) — handlers should only run on user actions.
    if (!propagateHandler) return;
    onChange?.(nextValue);
  };

  if (!resolvedVisibility) {
    return null;
  }

  return (
    <div
      className="form-input-container"
      data-form-field={name}
      {...(fieldControlType && { 'data-form-field-type': fieldControlType })}
      style={
        {
          ['--form-item-span']: width ?? FORM_ITEM_FLEX_WIDTH_FALLBACK,
        } as CSSProperties
      }
    >
      {showLabel && (
        <InputLabel hint={hint ?? help} className="form-input-label">
          {mandatory && <div className="form-input-required-indicator">*</div>}
          {resolvedLabel}
        </InputLabel>
      )}
      <GridInput
        control={control}
        disabled={(allowEditable && editable) === false}
        error={error}
        label={label}
        name={name}
        openFullscreenReference={handleOpenFullscreenReference}
        onChange={handleChange}
        onTouch={onTouch}
        onFocusIn={onFocusIn}
        onFocusOut={onFocusOut}
        referenceDatasets={referenceDatasets}
        setError={setError}
        setReferenceDatasets={setReferenceDatasets}
        type={type}
        value={value}
        isFormInput={true}
        form={form}
        formData={formData}
        requestData={requestData}
      />
    </div>
  );
};

export default withFormVisibilityInit<FormInputHOCProps>(
  withEditableResolve<FormInputHOCProps>(FormInput)
);
